import { Banknote, Globe, Layers, Milestone } from "lucide-react";
import { useEffect, useId, useState } from "react";
import {
  useGetList,
  useListContext,
  useLocaleState,
  useTranslate,
} from "ra-core";
import { ToggleFilterButton } from "@/components/admin/toggle-filter-button";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { FilterCategory } from "../filters/FilterCategory";
import { ResponsiveFilters } from "../misc/ResponsiveFilters";
import { applyAmountFilter, readAmountFilter } from "./amountFilter";
import {
  activeCountries,
  type CountryCount,
  visibleCountries,
} from "./countryFilter";
import {
  categoryKey,
  countryName,
  HIDE_OTHER,
  phaseKey,
  projectCategories,
  projectPhases,
} from "./projectUtils";
import { useCoverage } from "./territories";
import { useInvited } from "./useInvited";
import { HelpTip } from "./HelpTip";

const MAX_COUNTRIES = 15;

export const ProjectListFilter = () => {
  return (
    // Colonne à gauche sur ordinateur, panneau ouvert par un bouton sur téléphone.
    <ResponsiveFilters>
      <div className="flex flex-col gap-8" data-tour="filters">
        <FilterCategory
          icon={<Milestone className="h-4 w-4" />}
          label="resources.projects.fields.phase"
          help={<HelpTip topic="phase" />}
        >
          {projectPhases.map((phase) => (
            <ToggleFilterButton
              className="w-full justify-between"
              label={phaseKey(phase)}
              key={phase}
              value={{ phase }}
            />
          ))}
        </FilterCategory>

        <FilterCategory
          icon={<Banknote className="h-4 w-4" />}
          label="resources.projects.amount.title"
          help={<HelpTip topic="amount_filter" />}
        >
          <AmountFilter />
        </FilterCategory>

        <FilterCategory
          icon={<Layers className="h-4 w-4" />}
          label="resources.projects.fields.category"
          help={<HelpTip topic="category" />}
        >
          {projectCategories.map((category) => (
            <ToggleFilterButton
              className="w-full justify-between"
              label={categoryKey(category)}
              key={category}
              // Demander la catégorie « autre » lève le masquage, sinon la liste serait vide.
              value={
                category === "other"
                  ? { category, "category@neq": undefined }
                  : { category }
              }
            />
          ))}
          <ToggleFilterButton
            className="w-full justify-between"
            label="resources.projects.hide_other"
            value={HIDE_OTHER}
          />
        </FilterCategory>

        <FilterCategory
          icon={<Globe className="h-4 w-4" />}
          label="resources.projects.fields.country"
        >
          <CountryFilters />
        </FilterCategory>
      </div>
    </ResponsiveFilters>
  );
};

// Montant minimum en millions d'euros (estimation au dernier cours) ; appliqué en quittant le champ ou par Entrée.
const AmountFilter = () => {
  const translate = useTranslate();
  const id = useId();
  const { filterValues, setFilters } = useListContext();
  const current = readAmountFilter(filterValues ?? {});
  const [text, setText] = useState(current ? String(current.millions) : "");
  const includeUnknown = current?.includeUnknown ?? true;
  // Étiquette retirée ou filtre venu d'une question : le champ suit la liste.
  useEffect(() => {
    setText(current ? String(current.millions) : "");
  }, [current?.millions]); // eslint-disable-line react-hooks/exhaustive-deps

  const apply = (value: string, unknown: boolean) => {
    const millions = Number(value.replace(",", "."));
    setFilters(
      applyAmountFilter(
        filterValues ?? {},
        value.trim() && Number.isFinite(millions) ? millions : null,
        unknown,
      ),
    );
  };
  return (
    <div className="flex w-full flex-col gap-2 py-1">
      <div className="flex items-center gap-2">
        <Label htmlFor={`${id}-min`} className="text-sm font-normal">
          {translate("resources.projects.amount.min")}
        </Label>
        <Input
          id={`${id}-min`}
          inputMode="decimal"
          value={text}
          onChange={(event) => setText(event.target.value)}
          onBlur={() => apply(text, includeUnknown)}
          onKeyDown={(event) => {
            if (event.key === "Enter") apply(text, includeUnknown);
          }}
          placeholder="0"
          className="h-8 w-20 tabular-nums"
        />
        <span className="text-sm text-muted-foreground">M€</span>
      </div>
      <div className="flex items-center gap-2">
        <Checkbox
          id={`${id}-unknown`}
          checked={includeUnknown}
          disabled={!current}
          onCheckedChange={(checked) => apply(text, checked === true)}
        />
        <Label
          htmlFor={`${id}-unknown`}
          className="text-sm font-normal text-muted-foreground"
        >
          {translate("resources.projects.amount.include_unknown")}
        </Label>
      </div>
    </div>
  );
};

// Les plus fournis d'abord ; les autres par la recherche ou le bouton « Tous les pays ».
const CountryFilters = () => {
  const [locale = "fr"] = useLocaleState();
  const translate = useTranslate();
  const { filterValues } = useListContext();
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(false);
  // Invité (spec § 3, aperçu de la base) : tous les pays recensés, avec leur nombre de fiches sur toute la base ;
  // la liste ne montre que les siennes, la note sous la liste dit combien d'autres répondent au filtre.
  const invited = useInvited();
  const { data: own = [] } = useGetList<CountryCount>(
    "project_countries",
    {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "nb_projects", order: "DESC" },
    },
    { enabled: !invited },
  );
  const { data: coverage } = useCoverage(invited);
  const countries: CountryCount[] = invited
    ? (coverage ?? [])
        .map((row) => ({ id: row.country, nb_projects: row.projects }))
        .sort((a, b) => b.nb_projects - a.nb_projects)
    : own;
  const shown = visibleCountries(countries, {
    max: MAX_COUNTRIES,
    expanded,
    query,
    active: activeCountries(filterValues ?? {}),
    locale,
  });
  return (
    <>
      {invited && (
        <p className="px-3 pb-1 text-xs text-muted-foreground">
          {translate("resources.projects.teaser.countries_hint")}
        </p>
      )}
      {countries.length > MAX_COUNTRIES && (
        <Input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={translate("resources.projects.country_search")}
          aria-label={translate("resources.projects.country_search")}
          className="h-8 mb-1"
        />
      )}
      {shown.map((country) => (
        <ToggleFilterButton
          className="w-full justify-between"
          label={
            <span>
              {countryName(country.id, locale)}{" "}
              <span className="text-muted-foreground">
                {country.nb_projects}
              </span>
            </span>
          }
          key={country.id}
          value={{ country: country.id }}
        />
      ))}
      {query && !shown.length && (
        <p className="text-sm text-muted-foreground px-3 py-1">
          {translate("resources.projects.country_none")}
        </p>
      )}
      {!query && countries.length > MAX_COUNTRIES && (
        <Button
          variant="link"
          size="sm"
          className="justify-start px-3 h-auto py-1"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded
            ? translate("resources.projects.country_less")
            : translate("resources.projects.country_all", {
                count: countries.length,
              })}
        </Button>
      )}
    </>
  );
};
